import { WidgetPageView } from "@norn/widget-renderer/page"
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { AppWindow, RotateCw } from "lucide-react"
import { useMemo } from "react"
import { Trans } from "react-i18next"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton, UnavailableBlock } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { loadEvaluator } from "@/lib/checker"
import { useLocale, useT } from "@/lib/i18n"
import { useDefinitions } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { AdminWidgetPresentation } from "@/widget/catalog"
import { decodeContext } from "@/widget/context"
import { installedApps } from "@/widget/model"
import { useAdminWidgetHost } from "@/widget/runtime"

export function AppsIndexPage() {
  const t = useT()
  const scope = useScope()
  const defs = useDefinitions(scope)
  const apps = useMemo(() => (defs.data ? installedApps(defs.data) : []), [defs.data])
  return (
    <>
      <PageHeader crumbs={[t("nav.apps")]} />
      <div className="flex-1 overflow-y-auto">
        <Screen width="wide" className="px-4 py-6 md:px-6">
          <ContentHeader description={t("apps.description")} />
          {defs.isPending ? (
            <TableSkeleton rows={3} columns={2} />
          ) : defs.isError ? (
            <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} inline />
          ) : apps.length === 0 ? (
            <EmptyState icon={<AppWindow />} title={t("apps.noneInstalled")}>
              <p>{t("apps.installHelp")}</p>
            </EmptyState>
          ) : (
            <Section>
              <HairlineTable
                rows={apps}
                getRowKey={(app) => app.appId}
                minWidthClassName="min-w-[480px]"
                columns={[
                  {
                    key: "app",
                    header: t("nav.apps"),
                    cell: (app) => (
                      <Link
                        to="/$orgId/$environmentId/apps/$appId/$pageId"
                        params={{
                          ...scope,
                          appId: app.appId,
                          pageId: app.ux.app?.entryPageId ?? app.ux.pages[0]?.pageId ?? "",
                        }}
                        search={{}}
                        className="font-medium hover:text-primary"
                      >
                        {app.name}
                      </Link>
                    ),
                  },
                  {
                    key: "id",
                    header: t("definitionSummary.key"),
                    cell: (app) => <code className="font-mono text-2xs text-muted-foreground">{app.appId}</code>,
                  },
                  {
                    key: "pages",
                    header: t("overview.total"),
                    align: "end",
                    cell: (app) => (
                      <span className="text-muted-foreground">{t("apps.pages", { count: app.ux.pages.length })}</span>
                    ),
                  },
                  {
                    key: "layer",
                    header: t("definitions.layer"),
                    cell: (app) =>
                      app.artifact.layer === "registry" ? (
                        <Badge tone="outline">{t("common.registry")}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t("common.tenant")}</span>
                      ),
                  },
                ]}
              />
            </Section>
          )}
        </Screen>
      </div>
    </>
  )
}

export function AppPage() {
  const t = useT()
  const { preference } = useLocale()
  const scope = useScope()
  const { appId, pageId } = useParams({ from: "/$orgId/$environmentId/apps/$appId/$pageId" })
  const search = useSearch({ from: "/$orgId/$environmentId/apps/$appId/$pageId" })
  const navigate = useNavigate()
  const defs = useDefinitions(scope)
  const app = useMemo(
    () => (defs.data ? installedApps(defs.data).find((a) => a.appId === appId) : undefined),
    [defs.data, appId],
  )
  const page = app?.ux.pages.find((p) => p.pageId === pageId)
  const raw = search as Record<string, string>

  if (defs.isPending) return <TableSkeleton />
  if (defs.isError)
    return <ErrorState error={defs.error} what={t("apps.thisApp")} onRetry={() => void defs.refetch()} />
  if (!app || !page) {
    return (
      <EmptyState icon={<AppWindow />} title={app ? t("apps.noSuchPage") : t("apps.noSuchApp")}>
        <p>
          <Trans
            shouldUnescape
            tOptions={{ interpolation: { escapeValue: true } }}
            t={t}
            i18nKey="apps.notDeclared"
            values={{ id: `${appId}${app ? ` / ${pageId}` : ""}`, version: defs.data.version }}
            components={{ widgetValue: <code /> }}
          />
        </p>
      </EmptyState>
    )
  }
  const decoded = decodeContext(page, raw, preference.locale)
  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="a" to="/$orgId/$environmentId/apps" params={scope}>
            {t("nav.apps")}
          </Link>,
          app.name,
          page.name,
        ]}
        actions={
          <Button size="icon" variant="ghost" onClick={() => void defs.refetch()} aria-label={t("common.refresh")}>
            <RotateCw />
          </Button>
        }
      />
      <div className="flex-1 overflow-y-auto">
        <Screen width="board" className="max-w-7xl px-4 py-6 md:px-6" data-app-id={appId} data-page-id={pageId}>
          {!decoded.ok ? (
            <UnavailableBlock
              why={{ reason: t("apps.invalidParameters", { problems: decoded.problems.join("; ") }) }}
            />
          ) : (
            <PageBody
              key={`${appId}/${pageId}/${new URLSearchParams(raw).toString()}`}
              app={app}
              page={page}
              context={decoded.values}
              onNavigate={(p, params) =>
                void navigate({
                  to: "/$orgId/$environmentId/apps/$appId/$pageId",
                  params: { ...scope, appId, pageId: p },
                  search: params,
                })
              }
            />
          )}
        </Screen>
      </div>
    </>
  )
}

function PageBody({
  app,
  page,
  context,
  onNavigate,
}: {
  app: NonNullable<ReturnType<typeof installedApps>[number]>
  page: NonNullable<ReturnType<typeof installedApps>[number]>["ux"]["pages"][number]
  context: Readonly<Record<string, unknown>>
  onNavigate: (pageId: string, params: Record<string, string>) => void
}) {
  const scope = useScope()
  const defs = useDefinitions(scope)
  const set = defs.data
  const { preference } = useLocale()
  const widgetId =
    typeof app.artifact.payload.widgetId === "string" ? app.artifact.payload.widgetId : app.artifact.artifactId
  const host = useAdminWidgetHost(scope, onNavigate, widgetId)
  if (!set) return null
  return (
    <AdminWidgetPresentation>
      <WidgetPageView
        appId={app.appId}
        ux={app.ux}
        page={page}
        set={set}
        scope={scope}
        context={context}
        locale={preference.locale}
        host={host}
        loadEvaluator={loadEvaluator}
      />
    </AdminWidgetPresentation>
  )
}
